import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { sv as svLocale } from "date-fns/locale";
import { ExternalLink, ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { sanitizePostHtml } from "@/lib/sanitize";
import { useLanguage } from "@/i18n/useLanguage";
import type { NewsArticle } from "@/lib/posts";

/** Scroll-linked progress bar across the top of the article. */
export const ReadingProgress = () => {
  const [pct, setPct] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      setPct(max > 0 ? Math.min(100, Math.max(0, (h.scrollTop / max) * 100)) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      aria-hidden
      className="fixed top-0 left-0 right-0 z-40 h-0.5 bg-transparent pointer-events-none"
    >
      <div
        className="h-full bg-gradient-to-r from-primary via-primary/80 to-primary/40 transition-[width] duration-150"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

interface Props {
  article: NewsArticle;
  /** Show the "Back to news" links. Hide in inline previews. */
  showBackLinks?: boolean;
  /** Fire when the external CTA is clicked. */
  onExternalClick?: () => void;
}

/**
 * Renders the public-facing article body. Shared between the live
 * `/news/:slug` page and the admin preview surfaces so what an editor
 * sees is exactly what gets shipped.
 */
const NewsArticleBody = ({ article, showBackLinks = true, onExternalClick }: Props) => {
  const { t } = useTranslation();
  const { lang, localize } = useLanguage();

  const dateLabel =
    lang === "sv"
      ? format(article.date, "d MMMM yyyy", { locale: svLocale })
      : format(article.date, "MMMM d, yyyy");

  const updatedDate = article.updatedAt ? new Date(article.updatedAt) : null;
  const showUpdated =
    updatedDate && updatedDate.getTime() - article.date.getTime() > 24 * 60 * 60 * 1000;
  const updatedLabel = updatedDate
    ? lang === "sv"
      ? format(updatedDate, "d MMMM yyyy", { locale: svLocale })
      : format(updatedDate, "MMMM d, yyyy")
    : "";

  const cleanHtml = sanitizePostHtml(article.bodyHtml ?? "");

  return (
    <article className="container mx-auto px-5 md:px-6 py-10 md:py-14 max-w-3xl">
      {showBackLinks && (
        <Link
          to={localize("/news")}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> {t("newsArticle.backToNewsShort")}
        </Link>
      )}

      <header className="mb-8">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4 text-sm text-muted-foreground">
          <Badge variant="outline">
            {t(`newsPage.categories.${article.category}`, { defaultValue: article.category })}
          </Badge>
          <span>{dateLabel}</span>
          {article.author && (
            <>
              <span aria-hidden className="text-muted-foreground/50">·</span>
              <span>{t("newsPage.card.by", { author: article.author })}</span>
            </>
          )}
        </div>
        <h1 className="text-3xl md:text-5xl font-bold tracking-tight mb-4 text-foreground">
          {article.title || t("newsArticle.untitled", { defaultValue: "Untitled" })}
        </h1>
        {article.excerpt && (
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
            {article.excerpt}
          </p>
        )}
        <div className="mt-6 h-px bg-gradient-to-r from-border via-border/60 to-transparent" />
      </header>

      {article.coverImageUrl && (
        <figure className="mb-10 -mx-5 md:mx-0">
          <div className="md:rounded-2xl overflow-hidden border border-border/60 shadow-lg shadow-black/20 bg-card aspect-[16/9]">
            <img
              src={article.coverImageUrl}
              alt={article.title}
              className="w-full h-full object-cover"
              width="1200"
              height="675"
            />
          </div>
        </figure>
      )}

      <div className="py-2">
        {article.isExternal ? (
          <div className="mx-auto max-w-[68ch]">
            {article.externalUrl && (
              <p className="text-base md:text-lg">
                <a
                  href={article.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onExternalClick}
                  className="inline-flex items-center gap-1.5 text-blue-400 hover:underline underline-offset-4"
                >
                  {t("newsArticle.readMore")}
                  <ExternalLink className="h-4 w-4" />
                </a>
              </p>
            )}
          </div>
        ) : (
          <div
            className={[
              "mx-auto max-w-[68ch]",
              "prose prose-lg max-w-none",
              "prose-p:text-foreground prose-li:text-foreground",
              "prose-headings:text-foreground prose-headings:font-bold prose-headings:tracking-tight",
              "prose-h2:mt-10 prose-h2:mb-4 prose-h3:mt-8 prose-h3:mb-3",
              "prose-strong:text-foreground",
              "prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline prose-a:underline-offset-4",
              "prose-blockquote:border-l-blue-400 prose-blockquote:text-foreground/80",
              "prose-img:rounded-xl prose-img:border prose-img:border-border/60",
              "prose-code:text-foreground prose-code:bg-muted/60 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none",
            ].join(" ")}
            dangerouslySetInnerHTML={{ __html: cleanHtml }}
          />
        )}
      </div>

      {showUpdated && (
        <p className="mt-6 text-xs text-muted-foreground text-center">
          {t("newsArticle.lastUpdated", { date: updatedLabel })}
        </p>
      )}

      {showBackLinks && (
        <div className="mt-12 pt-8 border-t border-border">
          <Button asChild variant="tertiary">
            <Link to={localize("/news")}>
              <ArrowLeft className="mr-2 h-4 w-4" /> {t("newsArticle.backToNews")}
            </Link>
          </Button>
        </div>
      )}
    </article>
  );
};

export default NewsArticleBody;
